import { Grid } from '@mui/material'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { EventDto } from '../../../interfaces/IEvent'
import InputField from '../../../components/InputFields/InputField'
import AutocompleteField from '../../../components/InputFields/AutocompleteField'
import DateTimePickerField from '../../../components/InputFields/DateTimePickerField'
import { EventType, IncidentCategory, IncidentCriticality, IncidentStatus } from '../../../interfaces/IEventEnums'
import { FieldName } from '../../../interfaces/IFields'
import MonoInputField from '../../../components/InputFields/MonoInputField'

interface Props {
  onUpdate: (updatedEvent: EventDto) => void
  preDefinedFromDate?: Date
}

function CreateIncidentEventGrid({ onUpdate, preDefinedFromDate }: Props) {
  const [incidentEvent, setIncidentEvent] = useState({
    type: EventType.Fellesfeil,
    eventDetails: {
      title: '',
      status: '',
      fromDate: preDefinedFromDate ?? new Date(),
      toDate: null,
      description: '',
      category: IncidentCategory.Low,
      criticality: IncidentCriticality.A,
      responsible: {},
      externalReference: '',
    },
  } as EventDto)

  const setToDate = (incCriticality: string, fromDate: Date) => {
    const toDate = new Date(fromDate)

    if (incCriticality === IncidentCriticality.A) {
      toDate.setHours(toDate.getHours() + 3)
    } else if (incCriticality === IncidentCriticality.B) {
      toDate.setDate(toDate.getDate() + 1)
    } else if (incCriticality === IncidentCriticality.C) {
      toDate.setDate(toDate.getDate() + 3)
    }

    setIncidentEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        toDate: toDate,
      },
    }))
  }

  const handleSelectChange = useCallback((name: string, value: string | Date | null) => {
    setIncidentEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
      },
    }))
  }, [])

  const handleStartDateChange = useCallback((date: Date | null) => {
    if (!date) return
    setIncidentEvent((prevState) => {
      const eventToDate = prevState.eventDetails.toDate ? new Date(prevState.eventDetails.toDate) : null
      const toDate = eventToDate && date > eventToDate ? date : prevState.eventDetails.toDate
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          fromDate: date,
          toDate,
        },
      }
    })
  }, [])

  const handleEndDateChange = useCallback((date: Date | null) => {
    setIncidentEvent((prevState) => {
      const eventFromDate = new Date(prevState.eventDetails.fromDate)
      if (date && eventFromDate > date) {
        return {
          ...prevState,
          eventDetails: {
            ...prevState.eventDetails,
            toDate: eventFromDate,
          },
        }
      }
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          toDate: date,
        },
      }
    })
  }, [])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (name === FieldName.Name || name === FieldName.Id) {
      setIncidentEvent((prevState) => ({
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          responsible: {
            ...prevState.eventDetails.responsible,
            [name]: value,
          },
        },
      }))
      return
    }

    setIncidentEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
        responsible: {
          ...prevState.eventDetails.responsible,
        },
      },
    }))
  }, [])

  useEffect(() => {
    onUpdate(incidentEvent)
  }, [incidentEvent])

  useEffect(() => {
    setToDate(incidentEvent.eventDetails.criticality, incidentEvent.eventDetails.fromDate)
  }, [incidentEvent.eventDetails.fromDate, incidentEvent.eventDetails.criticality])

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 7,
        }}
      >
        <InputField
          name={FieldName.Title}
          title="Navn på hendelse"
          size="medium"
          value={incidentEvent.eventDetails.title}
          required={true}
          onChange={handleInputChange}
          maxLength={80}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 5,
        }}
      >
        <InputField
          name={FieldName.Name}
          title="Ansvarlig"
          size="medium"
          placeholder="Ansvarlig for retting"
          value={incidentEvent.eventDetails.responsible.name}
          onChange={handleInputChange}
          multiline={true}
          maxLength={30}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <AutocompleteField<IncidentStatus>
          name={FieldName.Status}
          title="Status"
          placeholder="Ingen valgt"
          options={Object.values(IncidentStatus)}
          onChange={(value: IncidentStatus | null) => handleSelectChange(FieldName.Status, value)}
          getOptionLabel={(status) => status}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 7,
          md: 3,
        }}
      >
        <AutocompleteField<IncidentCategory>
          name={FieldName.Category}
          title="Kategori"
          placeholder="Ingen valgt"
          value={incidentEvent.eventDetails.category as IncidentCategory}
          options={Object.values(IncidentCategory)}
          onChange={(value: IncidentCategory | null) => handleSelectChange(FieldName.Category, value)}
          getOptionLabel={(category) => category}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 5,
          md: 3,
        }}
      >
        <AutocompleteField<IncidentCriticality>
          name={FieldName.Criticality}
          title="Alvorlighetsgrad"
          placeholder="Ingen valgt"
          value={incidentEvent.eventDetails.criticality as IncidentCriticality}
          options={Object.values(IncidentCriticality)}
          onChange={(value: IncidentCriticality | null) => handleSelectChange(FieldName.Criticality, value)}
          getOptionLabel={(criticality) => criticality}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <DateTimePickerField
          name={FieldName.FromDate}
          title="Startdato"
          required={true}
          value={incidentEvent.eventDetails.fromDate}
          onChange={handleStartDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <DateTimePickerField
          name={FieldName.ToDate}
          title="Forventet rettetid"
          disablePast={true}
          minDateTime={incidentEvent.eventDetails.fromDate}
          value={incidentEvent.eventDetails.toDate}
          onChange={handleEndDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 12,
        }}
      >
        <InputField
          name={FieldName.ExternalReference}
          title="Ekstern referanse"
          size="medium"
          value={incidentEvent.eventDetails.externalReference}
          onChange={handleInputChange}
          multiline={true}
          maxLength={25}
        />
      </Grid>
      <Grid size={12}>
        <MonoInputField
          name={FieldName.Description}
          title="Beskrivelse"
          size="medium"
          value={incidentEvent.eventDetails.description}
          onChange={handleInputChange}
          multiline={true}
          rows={6}
          maxLength={1000}
        />
      </Grid>
    </>
  )
}

export default CreateIncidentEventGrid
